import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Layout as AntLayout,
  Menu,
  Button,
  Badge,
  Avatar,
  Typography,
  Space,
  Dropdown,
  Tooltip,
} from 'antd';
import type { MenuProps } from 'antd';
import {
  DashboardOutlined,
  ShopOutlined,
  TeamOutlined,
  DollarOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  CarOutlined,
  BellOutlined,
  SettingOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  LogoutOutlined,
  UserOutlined,
  BankOutlined,
  ApartmentOutlined,
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { notificationService } from '../services/api';
import browserNotificationService from '../services/browserNotificationService';

const { Header: AntHeader, Content, Sider } = AntLayout;
const { Text } = Typography;

const Layout: React.FC = () => {
  const { user, tenant, logout, isSuperAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationPermissionGranted, setNotificationPermissionGranted] = useState(false);
  const lastCheckedNotificationIdsRef = useRef<Set<string>>(new Set());

  // Admin menu items
  const adminMenuItems: MenuProps['items'] = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
    },
    {
      key: '/shopkeepers',
      icon: <ShopOutlined />,
      label: 'Shopkeepers',
    },
    {
      key: '/staff',
      icon: <TeamOutlined />,
      label: 'Staff',
    },
    {
      key: '/payments',
      icon: <DollarOutlined />,
      label: 'Payments',
    },
    {
      key: '/complaints',
      icon: <FileTextOutlined />,
      label: 'Complaints',
    },
    {
      key: '/attendance',
      icon: <CheckCircleOutlined />,
      label: 'Attendance',
    },
    {
      key: '/vehicles',
      icon: <CarOutlined />,
      label: 'Vehicles',
    },
    {
      key: '/noc-requests',
      icon: <FileTextOutlined />,
      label: 'NOC Requests',
    },
    {
      key: '/notifications',
      icon: <BellOutlined />,
      label: 'Notifications',
    },
    {
      key: '/settings',
      icon: <SettingOutlined />,
      label: 'Settings',
    },
  ];

  // Super Admin menu items
  const superAdminMenuItems: MenuProps['items'] = [
    {
      key: '/super-admin/dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
    },
    {
      key: '/super-admin/tenants',
      icon: <ApartmentOutlined />,
      label: 'Tenants',
    },
  ];

  const menuItems = isSuperAdmin ? superAdminMenuItems : adminMenuItems;
  const panelTitle = isSuperAdmin ? 'Super Admin' : (tenant?.name || 'Admin Panel');
  const safeUnreadCount = Number.isFinite(Number(unreadCount)) ? Math.max(0, Number(unreadCount)) : 0;

  const loadNotifications = useCallback(async (showBrowserNotifications = false) => {
    try {
      const res = await notificationService.list({ unreadOnly: true });
      if (res.data?.success) {
        const newNotifications = res.data.data.notifications || [];
        const newUnreadCountRaw = res.data.data.unreadCount;
        const newUnreadCount = Number.isFinite(Number(newUnreadCountRaw))
          ? Math.max(0, Number(newUnreadCountRaw))
          : 0;

        if (showBrowserNotifications && notificationPermissionGranted) {
          newNotifications.forEach((notification: any) => {
            if (!lastCheckedNotificationIdsRef.current.has(notification._id) && !notification.read) {
              browserNotificationService.showNotificationForItem(notification);
            }
          });

          newNotifications.forEach((notification: any) => {
            lastCheckedNotificationIdsRef.current.add(notification._id);
          });
        }

        setUnreadCount(newUnreadCount);
      } else {
        setUnreadCount(0);
      }
    } catch (error) {
      console.error('Error loading notifications', error);
      setUnreadCount(0);
    }
  }, [notificationPermissionGranted]);

  useEffect(() => {
    const requestPermission = async () => {
      if (!isSuperAdmin) {
        const permission = await browserNotificationService.requestPermission();
        setNotificationPermissionGranted(permission.granted);
      }
    };
    requestPermission();
  }, [isSuperAdmin]);

  useEffect(() => {
    if (isSuperAdmin) return;
    loadNotifications(false);
  }, [isSuperAdmin, loadNotifications]);

  useEffect(() => {
    if (isSuperAdmin) return;
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadNotifications(true);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isSuperAdmin, loadNotifications]);

  useEffect(() => {
    if (isSuperAdmin) return;
    const handleFcmMessage = () => {
      loadNotifications(true);
    };
    window.addEventListener('admin:fcm-message', handleFcmMessage);
    return () => {
      window.removeEventListener('admin:fcm-message', handleFcmMessage);
    };
  }, [isSuperAdmin, loadNotifications]);

  useEffect(() => {
    if (isSuperAdmin) return;
    if (location.pathname === '/notifications/history') {
      loadNotifications(false);
    }
  }, [isSuperAdmin, location.pathname, loadNotifications]);

  useEffect(() => {
    if (isSuperAdmin && !location.pathname.startsWith('/super-admin')) {
      navigate('/super-admin/dashboard', { replace: true });
    } else if (!isSuperAdmin && location.pathname.startsWith('/super-admin')) {
      navigate('/dashboard', { replace: true });
    }
  }, [isSuperAdmin, location.pathname, navigate]);

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
  };

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'user-info',
      label: (
        <div className="px-2 py-1">
          <Text strong className="block">{user?.name}</Text>
          <Text type="secondary" className="text-xs capitalize">
            {user?.role?.replace('_', ' ')}
          </Text>
        </div>
      ),
      disabled: true,
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      danger: true,
      onClick: logout,
    },
  ];

  return (
    <AntLayout className="min-h-screen">
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        width={260}
        className="shadow-lg"
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
        }}
        theme="dark"
      >
        <div className="flex flex-col h-full">
          {/* Sidebar Header */}
          <div className="p-4 border-b border-white/10">
            <div className="flex items-center justify-between">
              {!collapsed && (
                <div className="flex items-center gap-2">
                  <BankOutlined className="text-2xl text-blue-400" />
                  <Text className="text-white text-lg font-bold">{panelTitle}</Text>
                </div>
              )}
              {collapsed && (
                <BankOutlined className="text-2xl text-blue-400 mx-auto" />
              )}
            </div>
            {!collapsed && tenant && (
              <div className="mt-3">
                <Text className="text-white/70 text-xs">Tenant Code</Text>
                <div className="mt-1">
                  <span className="bg-white/15 px-2 py-1 rounded text-xs font-semibold">
                    {tenant.code}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Navigation Menu */}
          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={[location.pathname]}
            items={menuItems}
            onClick={handleMenuClick}
            className="flex-1 border-none"
          />

          {/* User Profile Section */}
          <div className="p-4 border-t border-white/10">
            {!collapsed ? (
              <div className="flex items-center gap-3">
                <Avatar icon={<UserOutlined />} className="bg-blue-500" />
                <div className="flex-1 min-w-0">
                  <Text className="text-white block truncate text-sm font-medium">
                    {user?.name}
                  </Text>
                  <Text className="text-white/70 text-xs capitalize block">
                    {user?.role?.replace('_', ' ')}
                  </Text>
                </div>
              </div>
            ) : (
              <div className="flex justify-center">
                <Avatar icon={<UserOutlined />} className="bg-blue-500" />
              </div>
            )}
          </div>
        </div>
      </Sider>

      <AntLayout style={{ marginLeft: collapsed ? 80 : 260, transition: 'margin-left 0.2s' }}>
        <AntHeader className="bg-white px-6 shadow-sm flex items-center justify-between">
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            className="text-lg"
          />

          <Space size="large">
            {!isSuperAdmin && (
              safeUnreadCount > 0 ? (
                <Badge count={safeUnreadCount} size="small">
                  <Button
                    type="text"
                    icon={<BellOutlined className="text-xl" />}
                    className="flex items-center justify-center"
                    onClick={() => navigate('/notifications/history')}
                  />
                </Badge>
              ) : (
                <Button
                  type="text"
                  icon={<BellOutlined className="text-xl" />}
                  className="flex items-center justify-center"
                  onClick={() => navigate('/notifications/history')}
                />
              )
            )}

            {tenant && !isSuperAdmin && (
              <Tooltip title={`Tenant: ${tenant.name}`}>
                <span className="bg-blue-600 text-white px-3 py-1 rounded text-xs font-semibold">
                  {tenant.code}
                </span>
              </Tooltip>
            )}

            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <Space className="cursor-pointer hover:bg-gray-50 px-3 py-1 rounded-lg transition-colors">
                <Avatar icon={<UserOutlined />} className="bg-blue-500" />
                {!collapsed && (
                  <div className="flex flex-col items-end">
                    <Text strong className="text-sm">{user?.name}</Text>
                    <Text type="secondary" className="text-xs capitalize">
                      {user?.role?.replace('_', ' ')}
                    </Text>
                  </div>
                )}
              </Space>
            </Dropdown>
          </Space>
        </AntHeader>

        <Content className="bg-gray-50 min-h-screen p-6">
          <Outlet />
        </Content>
      </AntLayout>
    </AntLayout>
  );
};

export default Layout;
